/**
 * useImport
 *
 * Hook that orchestrates client-side xlsx/csv ingestion.
 *
 * Deduplication rules (per spec Section 3h):
 *  - If the same UPC or ASIN appears multiple times in the file, only the
 *    LAST occurrence is processed; earlier ones are silently skipped.
 *  - If UPC/ASIN already exists in the DB, the record is updated (ISG SKU preserved).
 *  - Products with no UPC and no ASIN are always inserted as new.
 *
 * 100K+ row strategy:
 *  - All rows are mapped in one pass before any DB calls.
 *  - A single pre-fetch loads all matching UPCs/ASINs from the DB.
 *  - Rows are upserted in BATCH_SIZE chunks with async yield between batches.
 */

import { useState, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import type { WorkBook, WorkSheet } from 'xlsx';
import { supabase } from '../lib/supabase';
import {
  buildColumnIndexFromRegistry,
  mapRowToProduct,
  findHeaderRow,
} from '../lib/algopix-columns';
import type { MappedAlgopixProduct } from '../lib/algopix-columns';
import type { Database, Json } from '../lib/database.types';
import { PROVENANCE_SOURCE_SHEET_IMPORT } from '../lib/provenanceSources';
import { isNormalizedUpc, normalizeGtin } from '../lib/upcNormalize';
import { importUnitCost, resolveImportMarkupPct, type ImportMarkupSettings } from '../lib/markup';
import { resolveClearanceRow, type MastersDedupMap, type AliasMap } from '../lib/clearanceResolve';
import { normalizeBrand, extractPartNoSet } from '../lib/matchScore';

// C5 — the set-based materialization RPC's return shape (lands in database.types.ts at the Phase-5 regen).
type MaterializeResult = { ok: boolean; inserted_ids: string[]; resolved_ids: string[] };
type ImportUpdate = Database['public']['Tables']['ingestion_batches']['Update'];

// K5.2a/b — ingestion mode. price_sheet = the W1 dedup-and-UPSERT (one manufacturer set once,
// markup applied). supplier_sheet = clearance: the synchronous EXACT resolution funnel (rungs 1–3:
// UPC -> part#-exact brand-qualified -> alias-exact; three-outcome resolve/insert/hold; markup 0;
// no manufacturer set-once — resolved per-row; fuzzy is queue-side only, never at ingestion).
export type SheetMode = 'price_sheet' | 'supplier_sheet';

const BATCH_SIZE = 200;

/** Coerce an unknown to a non-empty trimmed string, else null (the shared identity/queue-write helper). */
const asStr = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null);

// C5 — the per-field provenance moved INTO importFile. The products-side template (productProvenance)
// covers the two NON-cost tracked fields (algopix_data, map_price); the provenance_products_capture
// trigger enriches them with set_at + set_by on INSERT and previous_value on UPDATE. cost_usd provenance
// is now JUNCTION-sourced — write_junction_cost writes the full 5-key envelope (the markup % as its reason)
// and the sync trigger read-copies it to products.provenance.cost_usd. Go-forward source =
// PROVENANCE_SOURCE_SHEET_IMPORT (the neutral C5 shed; PROVENANCE_SOURCE_ALGOPIX_IMPORT retained for
// historical rows, no backfill — Conv-51 / Decision K).

// ── Types ─────────────────────────────────────────────────────────────────────

type ImportStatus = 'idle' | 'parsing' | 'deduping' | 'processing' | 'complete' | 'error';

interface ImportProgress {
  current: number;
  total: number;
  pct: number;
}

interface ImportError {
  row: number | string;
  friendly: string;
  technical: string;
}

interface ImportSummary {
  total: number;
  inserted: number;
  updated: number;
  skipped: number;
  /** C4 — price_sheet rows the funnel could not confidently dispose (>=2 masters / no clean anchor) → intake_resolution_queue. */
  held: number;
  errors: number;
  matched: number;
  resolvedFlags: number;
  fileName: string;
  sheetName: string;
}

interface TranslatedError {
  friendly: string;
  technical: string;
}

export interface UseImportResult {
  importFile: (
    fileName: string,
    buffer: ArrayBuffer,
    sheetName: string,
    distributorId: string | null,
    manufacturerId: string | null,
    userId: string | null,
    markupOverride?: number | null,
    sheetMode?: SheetMode,
    upstreamSourceText?: string | null
  ) => Promise<void>;
  status: ImportStatus;
  progress: ImportProgress;
  summary: ImportSummary | null;
  errors: ImportError[];
  reset: () => void;
}

// ── Sheet detection ───────────────────────────────────────────────────────────

/**
 * Synchronously detect sheet names from an ArrayBuffer.
 * Uses bookSheets mode — reads structure only, very fast.
 */
export function detectSheets(buffer: ArrayBuffer): string[] {
  const wb: WorkBook = XLSX.read(buffer, { type: 'array', bookSheets: true });
  return wb.SheetNames;
}

// ── Error translation ─────────────────────────────────────────────────────────

function translateError(raw: string | null | undefined): TranslatedError {
  const msg = raw ?? '';
  if (msg.includes('cannot affect row a second time'))
    return { friendly: 'Duplicate record in batch — resolved by deduplication', technical: msg };
  if (msg.includes('duplicate key value'))
    return { friendly: 'Duplicate value conflicts with an existing record', technical: msg };
  if (msg.includes('not-null constraint'))
    return { friendly: 'A required field is missing', technical: msg };
  if (msg.includes('invalid input syntax'))
    return { friendly: 'Invalid data format in one or more fields', technical: msg };
  if (msg.includes('permission denied'))
    return { friendly: 'Permission denied — check your account role', technical: msg };
  if (msg.includes('JWT') || msg.includes('auth'))
    return { friendly: 'Session expired — please refresh and try again', technical: msg };
  return { friendly: msg, technical: msg };
}

// ── K5.2a/b — masters-only identity index for the clearance resolver (rungs 1–2) ─────
// ALL masters (parent_product_id IS NULL; ~1955 today, range-paged so the pull is NEVER silently
// truncated by the PostgREST 1000-row cap). K5.2a fetched only file-UPC/ASIN-matching masters; rung 2
// (part#-exact) must reach masters that share NO UPC with a cryptic row, so K5.2b pulls every master
// and builds the keys TS-side. Keys: upc:/asin: (rung 1, behavior unchanged) + partno:<brandFold>:<pn>
// (rung 2) where brandFold = matchScore.normalizeBrand(brand) (lower THEN strip — GOTCHA 1, never
// SQL-side strip-then-lower) and pn = each normalizePartNo token from the {supplier_part_no, mpn,
// model} union. Masters deduped by id; multiplicity honest (>=2 distinct masters on a key -> HOLD).
async function buildMastersDedupMap(): Promise<{
  map: MastersDedupMap;
  /** C4 — master id -> its provenance, for the standing funnel's resolve/upsert smart-spread merge
   *  (replaces buildDedupMap's DedupEntry.provenance carry; isg_sku is unneeded — the upsert is id-keyed). */
  provenanceById: Map<string, Record<string, Record<string, unknown>> | null>;
}> {
  type MasterIdentity = {
    upc: string | null;
    asin: string | null;
    brand: string | null;
    model: string | null;
    mpn: string | null;
    supplier_part_no: string | null;
    condition_grade: string; // C1 identity axis (NOT NULL DEFAULT 'new')
  };
  const masterRows = new Map<string, MasterIdentity>();
  const provenanceById = new Map<string, Record<string, Record<string, unknown>> | null>();
  const PAGE = 1000;
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase
      .from('products')
      .select('id, upc, asin, brand, model, mpn, supplier_part_no, condition_grade, provenance')
      .is('parent_product_id', null)
      .order('id', { ascending: true })
      .range(from, from + PAGE - 1);
    if (error) throw new Error(`Failed to load masters for resolution: ${error.message}`);
    if (!data || data.length === 0) break;
    for (const p of data) {
      masterRows.set(p.id, {
        upc: p.upc,
        asin: p.asin,
        brand: p.brand,
        model: p.model,
        mpn: p.mpn,
        supplier_part_no: p.supplier_part_no,
        condition_grade: p.condition_grade,
      });
      provenanceById.set(
        p.id,
        (p.provenance as Record<string, Record<string, unknown>> | null) ?? null
      );
    }
    if (data.length < PAGE) break;
  }

  const map: MastersDedupMap = new Map();
  const push = (key: string, id: string): void => {
    const arr = map.get(key);
    if (arr) arr.push(id);
    else map.set(key, [id]);
  };
  for (const [id, r] of masterRows) {
    // C1/C3 — grade-segment all key families (`<type>:<grade>:<val>`). condition_grade is NOT NULL
    // (DB DEFAULT 'new'). Rung 1 PRIMARY = gtin: (C3 — UPC-12 + EAN-13 fold to one canonical-14 via
    // normalizeGtin, the SAME fn resolveClearanceRow looks up with, so cross-form pairs collide).
    // Rung 1 SECONDARY = asin: (consulted only on a gtin miss/absent).
    const gtin = normalizeGtin(r.upc);
    if (gtin) push(`gtin:${r.condition_grade}:${gtin}`, id);
    if (r.asin) push(`asin:${r.condition_grade}:${r.asin}`, id);
    const brandFold = normalizeBrand(r.brand);
    if (brandFold !== null) {
      for (const pn of extractPartNoSet({
        supplierPartNo: r.supplier_part_no,
        mpn: r.mpn,
        model: r.model,
      })) {
        push(`partno:${r.condition_grade}:${brandFold}:${pn}`, id);
      }
    }
  }
  return { map, provenanceById };
}

// ── K5.2b — distributor-scoped alias index for the clearance resolver (rung 3) ─────
// The learned (distributor, supplier-SKU) -> master cross-reference, seeded by resolve_clearance_hold.
// Key = the RAW seed value BYTE-FOR-BYTE (no trim, no normalize — GOTCHA 2 / read-mirrors-seed; the
// seed is COALESCE(NULLIF(supplier_part_no,''), NULLIF(model,'')) on the queue's stored values). The
// caller passes the SAME asStr'd row values the queue stored, so the resolver's rawAliasKey reproduces
// this key exactly. UNIQUE(distributor_id, alias_sku) + distributor-scope -> one master per key.
// Range-paged (0 rows today; grows as humans resolve holds) so a large alias set is never truncated.
async function buildAliasMap(distributorId: string): Promise<AliasMap> {
  const map: AliasMap = new Map();
  const PAGE = 1000;
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase
      .from('product_aliases')
      .select('alias_sku, master_product_id')
      .eq('distributor_id', distributorId)
      .order('alias_sku', { ascending: true })
      .range(from, from + PAGE - 1);
    if (error)
      throw new Error(`Failed to load product aliases for clearance resolution: ${error.message}`);
    if (!data || data.length === 0) break;
    for (const a of data) map.set(a.alias_sku, a.master_product_id);
    if (data.length < PAGE) break;
  }
  return map;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useImport(): UseImportResult {
  const [status, setStatus] = useState<ImportStatus>('idle');
  const [progress, setProgress] = useState<ImportProgress>({ current: 0, total: 0, pct: 0 });
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [errors, setErrors] = useState<ImportError[]>([]);

  const importIdRef = useRef<string | null>(null);

  const reset = useCallback((): void => {
    setStatus('idle');
    setProgress({ current: 0, total: 0, pct: 0 });
    setSummary(null);
    setErrors([]);
    importIdRef.current = null;
  }, []);

  const importFile = useCallback(
    async (
      fileName: string,
      buffer: ArrayBuffer,
      sheetName: string,
      distributorId: string | null,
      manufacturerId: string | null,
      userId: string | null,
      markupOverride?: number | null,
      sheetMode?: SheetMode,
      upstreamSourceText?: string | null
    ): Promise<void> => {
      reset();
      const rowErrors: ImportError[] = [];
      const mode: SheetMode = sheetMode ?? 'price_sheet';

      try {
        // ── 1. Parse the selected sheet ───────────────────────────────────────
        setStatus('parsing');
        const workbook: WorkBook = XLSX.read(buffer, { type: 'array', cellDates: false });
        const sheet: WorkSheet | undefined = workbook.Sheets[sheetName];

        if (!sheet) throw new Error(`Sheet "${sheetName}" not found in workbook.`);

        // ── Header detection fix ───────────────────────────────────────────────
        // Algopix output files can have 1-2 metadata/category rows above the
        // real 106-column header row. sheet_to_json without header:1 uses ROW 1
        // as the header, which in practice contains only "AID" — leaving all
        // 105 other column fields unmapped and all extracted fields NULL.
        //
        // Fix: read the full 2D array, then find the real header row by picking
        // the row (within the first 5) that has the most non-empty string cells.
        // Rebuild row objects from that row downward.
        const rawMatrix = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          defval: null,
          raw: true,
        }) as unknown[][];
        if (rawMatrix.length < 2) throw new Error(`Sheet "${sheetName}" contains no data rows.`);

        // Find header row (shared findHeaderRow so the ingest column-map UI maps
        // the SAME header strings this import indexes).
        const headerRowIdx = findHeaderRow(rawMatrix);

        // Normalize header strings (trim, keep null for empty cells)
        const headers: (string | null)[] = (rawMatrix[headerRowIdx] ?? []).map((h) =>
          h !== null && h !== undefined ? String(h).trim() : null
        );

        // intentional diagnostic for header-row detection on Algopix uploads
        console.warn(
          '[useImport] headerRowIdx:',
          headerRowIdx,
          '| header count:',
          headers.filter(Boolean).length,
          '| first 8 headers:',
          headers.slice(0, 8)
        );

        // Build row objects from the rows after the header row
        const raw: Record<string, unknown>[] = rawMatrix
          .slice(headerRowIdx + 1)
          .map((dataRow) => {
            const obj: Record<string, unknown> = {};
            for (let i = 0; i < headers.length; i++) {
              const h = headers[i];
              if (h !== null) obj[h] = dataRow[i] ?? null;
            }
            return obj;
          })
          .filter((obj) => Object.values(obj).some((v) => v !== null)); // drop blank trailing rows

        if (raw.length === 0) throw new Error(`Sheet "${sheetName}" contains no data rows.`);

        // ── W1 — Load the per-distributor column-map registry (the load-bearing
        // mapping; replaces the global ALGOPIX_FIELD_MAP). Per-distributor and
        // human-confirmed (pipeline-plan §8 — no auto-guess of the cost column).
        if (!distributorId) {
          throw new Error(
            'A distributor is required to import — the column map is per-distributor.'
          );
        }
        const { data: registryRows, error: registryErr } = await supabase
          .from('column_map_registry')
          .select('canonical_field, sheet_header, is_cost_column')
          .eq('distributor_id', distributorId);
        if (registryErr)
          throw new Error(`Failed to load column-map registry: ${registryErr.message}`);
        if (!registryRows || registryRows.length === 0) {
          throw new Error(
            'No column mapping confirmed for this distributor — map the sheet columns first.'
          );
        }
        const columnFieldMap = buildColumnIndexFromRegistry(headers, registryRows);
        const totalRows = raw.length;

        // ── W1 — Resolve the buy-side import markup % (override > junction default
        // > 0) for the (ingestion-manufacturer, distributor) pair. Baked into
        // cost_usd below; the engine NEVER re-applies it (reads marked-up cost_usd).
        let junctionMarkup: number | null = null;
        if (manufacturerId) {
          const { data: mdRow } = await supabase
            .from('manufacturer_distributors')
            .select('markup_pct')
            .eq('manufacturer_id', manufacturerId)
            .eq('distributor_id', distributorId)
            .maybeSingle();
          junctionMarkup = (mdRow?.markup_pct as number | null | undefined) ?? null;
        }
        // supplier_sheet (clearance): the typed clearance price IS the cost — NEVER mark it up.
        // price_sheet: override > junction > 0. The rules live in src/lib/markup.ts
        // (resolveImportMarkupPct / importUnitCost), shared with the answer-key runner.
        const markupSettings: ImportMarkupSettings = {
          mode,
          perFileOverridePct: markupOverride ?? null,
          manufacturerMarkupPct: junctionMarkup,
        };
        const markupPct = resolveImportMarkupPct(markupSettings);

        // C5 — cost provenance moves to the junction: write_junction_cost writes the 5-key envelope and the
        // sync trigger read-copies it to products.provenance.cost_usd. The products-side template is the two
        // NON-cost fields only; the markup % travels as the cost envelope's `reason` (costReason, passed to
        // the RPC). Go-forward source = PROVENANCE_SOURCE_SHEET_IMPORT for all three (the two non-cost fields
        // here + the cost envelope's p_source at the RPC) — the neutral C5 shed. The smart-spread merge at
        // resolve (buildProvenance) preserves prior per-field envelopes.
        const productProvenance: Record<string, { source: string; reason: string | null }> = {
          algopix_data: { source: PROVENANCE_SOURCE_SHEET_IMPORT, reason: null },
          map_price: { source: PROVENANCE_SOURCE_SHEET_IMPORT, reason: null },
        };
        const costReason = markupPct > 0 ? `markup +${markupPct}% applied at import` : null;

        // ── 2. Create import record (W1: stamp the ingestion manufacturer + the
        // applied markup %; supplier_id retired) ──────────────────────────────
        const { data: importRec, error: importErr } = await supabase
          .from('ingestion_batches')
          .insert({
            file_name: fileName,
            row_count: totalRows,
            status: 'processing',
            distributor_id: distributorId,
            manufacturer_id: manufacturerId || null,
            markup_pct: markupPct,
            sheet_mode: mode,
            upstream_source_text: mode === 'supplier_sheet' ? upstreamSourceText || null : null,
            uploaded_by: userId || null,
          })
          .select()
          .single();

        if (importErr) throw new Error(`Failed to create import record: ${importErr.message}`);
        if (!importRec) throw new Error('Import record returned empty.');
        importIdRef.current = importRec.id;

        // ── 3. Map all rows ───────────────────────────────────────────────────
        setStatus('deduping');

        const mappedRows: (MappedAlgopixProduct | null)[] = [];
        // C5 — the pre-markup base per row (parallel to mappedRows). cost_sheet = the marked-up value;
        // cost_sheet_base = this snapshot, captured BEFORE applyMarkup overwrites mapped.cost_usd.
        const costBases: (number | null)[] = [];
        for (let i = 0; i < raw.length; i++) {
          try {
            const mapped = mapRowToProduct(raw[i], headers, columnFieldMap);
            const costBase = typeof mapped.cost_usd === 'number' ? mapped.cost_usd : null;
            // W1 — bake the resolved markup into cost_usd UPSTREAM (the engine
            // reads the marked-up value and must never re-apply markup).
            if (typeof mapped.cost_usd === 'number') {
              mapped.cost_usd = importUnitCost(mapped.cost_usd, markupSettings);
            }
            // W1 — surface UPCs that failed normalization (preserved raw; the
            // deterministic W2 lookup may miss them). Non-blocking — the row still
            // imports; this is data-quality visibility, not a hard error.
            if (
              typeof mapped.upc === 'string' &&
              mapped.upc !== '' &&
              !isNormalizedUpc(mapped.upc)
            ) {
              rowErrors.push({
                row: i + 2,
                friendly: `UPC "${mapped.upc}" is not a valid UPC/EAN — preserved raw; it may not match a listing.`,
                technical: `upc_normalize_invalid: ${mapped.upc}`,
              });
            }
            mappedRows.push(mapped);
            costBases.push(costBase);
          } catch (e) {
            const message = e instanceof Error ? e.message : String(e);
            rowErrors.push({
              row: i + 2,
              friendly: `Parse error: ${message}`,
              technical: message,
            });
            mappedRows.push(null);
            costBases.push(null);
          }
        }

        // ── 4. Pre-compute last-occurrence index per GTIN + ASIN ──────────────
        // C3/C4 — the WITHIN-FILE dedup: the SAME `gtin:new:` / `asin:new:` keys (via normalizeGtin) as
        // the per-row loop, so two cross-form rows (UPC-12 vs EAN-13) in ONE file collapse to a single
        // survivor instead of double-writing. (The CATALOG resolve is the C4 funnel via buildMastersDedupMap.)
        const lastOccurrence = new Map<string, number>();
        for (let i = 0; i < mappedRows.length; i++) {
          const p = mappedRows[i];
          if (!p) continue;
          const gtin = normalizeGtin(p.upc);
          if (gtin) lastOccurrence.set(`gtin:new:${gtin}`, i);
          if (p.asin) lastOccurrence.set(`asin:new:${p.asin}`, i);
        }
        // ── K5.2a/b — supplier_sheet (clearance) rides the synchronous EXACT resolution funnel ──
        // rungs 1–3 (UPC-exact -> part#-exact brand-qualified -> alias-exact), three-outcome:
        // resolve -> child lot under the master; insert -> master + first lot; hold -> the resolution
        // queue (human link-to-master). EXACT-ONLY at ingestion — fuzzy is queue-side only, never
        // auto-resolves. markup already forced 0 above. The price_sheet dedup-and-UPSERT below is the
        // unchanged W1 path.
        if (mode === 'supplier_sheet') {
          const now = new Date().toISOString();
          setStatus('processing');
          setProgress({ current: 0, total: totalRows, pct: 0 });
          const { map: mastersMap } = await buildMastersDedupMap();
          const aliasMap = await buildAliasMap(distributorId);
          let resolvedCount = 0;
          let insertedNewCount = 0;
          let heldCount = 0;

          for (let i = 0; i < mappedRows.length; i++) {
            const product = mappedRows[i];
            if (!product) continue;
            const srn = headerRowIdx + i + 2;
            const cost = typeof product.cost_usd === 'number' ? product.cost_usd : null;
            const decision = resolveClearanceRow(
              {
                upc: product.upc,
                asin: product.asin,
                supplier_part_no: asStr(product.supplier_part_no),
                model: asStr(product.model),
                brand: asStr(product.brand),
              },
              mastersMap,
              aliasMap
            );

            try {
              if (decision.outcome === 'resolve' && decision.masterId) {
                if (cost === null)
                  throw new Error('Clearance row has no cost — cannot create a lot');
                const { error } = await supabase.rpc('create_clearance_lot', {
                  p_master_id: decision.masterId,
                  p_cost_usd: cost,
                  p_import_id: importRec.id,
                  p_source_row_number: srn,
                });
                if (error) throw new Error(error.message);
                resolvedCount++;
              } else if (decision.outcome === 'insert') {
                if (cost === null)
                  throw new Error('Clearance row has no cost — cannot create a master + lot');
                const { data: cmlData, error } = await supabase.rpc(
                  'create_clearance_master_and_lot',
                  {
                    p_row: product as unknown as Json,
                    p_import_id: importRec.id,
                    p_distributor_id: distributorId,
                    p_source_row_number: srn,
                    p_cost_usd: cost,
                    p_condition: 'new', // C1 — standing/clearance ingestion is uniformly 'new' (no derivation yet)
                  }
                );
                if (error) throw new Error(error.message);
                insertedNewCount++;
                // J (clearance half) — augment the in-memory masters map with the NEW master's keys so a
                // later same-identity row in THIS file RESOLVES (attaches a lot) instead of double-inserting
                // a second master (the map is built once before the loop; supplier_sheet has no last-
                // occurrence skip). Mirrors buildMastersDedupMap's key construction (row-side: no mpn).
                const newMasterId = (cmlData as { master_id?: string } | null)?.master_id;
                if (newMasterId) {
                  const pushMaster = (key: string): void => {
                    const arr = mastersMap.get(key);
                    if (arr) arr.push(newMasterId);
                    else mastersMap.set(key, [newMasterId]);
                  };
                  const g = normalizeGtin(product.upc);
                  if (g) pushMaster(`gtin:new:${g}`);
                  const aZ = asStr(product.asin);
                  if (aZ) pushMaster(`asin:new:${aZ}`);
                  const bf = normalizeBrand(asStr(product.brand));
                  if (bf !== null) {
                    for (const pn of extractPartNoSet({
                      supplierPartNo: asStr(product.supplier_part_no),
                      model: asStr(product.model),
                    })) {
                      pushMaster(`partno:new:${bf}:${pn}`);
                    }
                  }
                }
              } else {
                const { error } = await supabase.from('intake_resolution_queue').insert({
                  import_id: importRec.id,
                  source_row_number: srn,
                  raw_row: product as unknown as Json,
                  upc: asStr(product.upc),
                  asin: asStr(product.asin), // C3 — extraction (asin only; ean is a sourceless forward-seam)
                  supplier_part_no: asStr(product.supplier_part_no),
                  model: asStr(product.model),
                  brand: asStr(product.brand),
                  title: asStr(product.title),
                  cost_usd: cost,
                });
                if (error) throw new Error(error.message);
                heldCount++;
              }
            } catch (e) {
              const message = e instanceof Error ? e.message : String(e);
              const { friendly, technical } = translateError(message);
              rowErrors.push({ row: i + 2, friendly, technical });
            }

            if (i % BATCH_SIZE === 0) {
              setProgress({ current: i, total: totalRows, pct: Math.round((i / totalRows) * 100) });
              setErrors([...rowErrors]);
              await new Promise<void>((r) => setTimeout(r, 0));
            }
          }

          const clearanceProcessed = resolvedCount + insertedNewCount;
          const clearanceStatus: ImportUpdate['status'] =
            rowErrors.length > 0 && clearanceProcessed + heldCount === 0 ? 'failed' : 'complete';
          const clearanceUpdate: ImportUpdate = {
            status: clearanceStatus,
            processed_count: clearanceProcessed,
            error_log:
              rowErrors.length > 0 ? (rowErrors as unknown as ImportUpdate['error_log']) : null,
            completed_at: now,
          };
          await supabase.from('ingestion_batches').update(clearanceUpdate).eq('id', importRec.id);

          setProgress({ current: totalRows, total: totalRows, pct: 100 });
          setSummary({
            total: totalRows,
            inserted: insertedNewCount, // new masters (+ their first lots)
            updated: resolvedCount, // lots attached under existing masters
            skipped: 0, // supplier_sheet runs every row through the funnel (no within-file skip)
            held: heldCount, // held for human link-to-master (intake_resolution_queue)
            errors: rowErrors.length,
            matched: 0,
            resolvedFlags: 0,
            fileName,
            sheetName,
          });
          setErrors([...rowErrors]);
          setStatus('complete');
          return;
        }

        // C4 — the standing price_sheet path joins the shared funnel. Parent-filtered masters map (excludes
        // lots — resolves the C3 lot-dedup-scope rider for free) + the per-master provenance side-map (the
        // resolve/upsert smart-spread carry, replacing buildDedupMap's DedupEntry) + the distributor alias
        // index (rung 3). buildDedupMap is retired.
        const { map: mastersMap, provenanceById } = await buildMastersDedupMap();
        const aliasMap = await buildAliasMap(distributorId);

        // ── 6. Batch insert / upsert ──────────────────────────────────────────
        setStatus('processing');
        setProgress({ current: 0, total: totalRows, pct: 0 });

        let insertedCount = 0;
        let updatedCount = 0;
        let skippedCount = 0; // within-file last-occurrence duplicates
        let heldCount = 0; // C4 — funnel holds (>=2 masters / no clean anchor) -> intake_resolution_queue
        const holdRows: Record<string, unknown>[] = [];
        const now = new Date().toISOString();
        // Accumulator for product IDs from successful insert/update batches.
        // Failed batches don't contribute. Drained into match_product_brands
        // after the loop to populate brand_id + auto-resolve unknown_brand flags.
        const matchedIds: string[] = [];

        for (let batchStart = 0; batchStart < mappedRows.length; batchStart += BATCH_SIZE) {
          const batch = mappedRows.slice(batchStart, batchStart + BATCH_SIZE);

          const insertByKey = new Map<string, Record<string, unknown>>();
          const updateById = new Map<string, Record<string, unknown>>();

          for (let j = 0; j < batch.length; j++) {
            const globalIdx = batchStart + j;
            const product = batch[j];
            if (!product) continue;

            // C3/C4 — the WITHIN-FILE dedup keys (via normalizeGtin), matching lastOccurrence; standing
            // rows uniformly 'new'. The two sites agree so two cross-form rows in one file collapse to one.
            const gtin = normalizeGtin(product.upc);
            const gtinKey = gtin ? `gtin:new:${gtin}` : null;
            const asinKey = product.asin ? `asin:new:${product.asin}` : null;

            const isLast =
              (!gtinKey || lastOccurrence.get(gtinKey) === globalIdx) &&
              (!asinKey || lastOccurrence.get(asinKey) === globalIdx);

            if (!isLast) {
              skippedCount++;
              continue;
            }

            // C4 — the standing price_sheet path JOINS the shared funnel (the auto-insert fix). Run the
            // SAME exact-only three-outcome resolver the supplier_sheet branch uses; partition resolve/
            // insert/hold. KEEP the batched write — the per-row standing RPCs serve the queue + API, NOT the
            // bulk loop (~2,275 round-trips, capture P5; the subsume to a set-based RPC is C5).
            const decision = resolveClearanceRow(
              {
                upc: product.upc,
                asin: product.asin,
                supplier_part_no: asStr(product.supplier_part_no),
                model: asStr(product.model),
                brand: asStr(product.brand),
              },
              mastersMap,
              aliasMap
            );

            // Day 84 P6.3 L14 F2 — smart-spread merge: preserve a matched master's prior per-field
            // provenance metadata (set_at / set_by / previous_value) the trigger skips on an unchanged
            // value. The master's prior provenance now comes from provenanceById (was buildDedupMap).
            const buildProvenance = (
              existingProv: Record<string, Record<string, unknown>> | null
            ): Record<string, Record<string, unknown>> => {
              const merged: Record<string, Record<string, unknown>> = { ...(existingProv ?? {}) };
              for (const field of Object.keys(productProvenance)) {
                merged[field] = { ...(merged[field] ?? {}), ...productProvenance[field] };
              }
              return merged;
            };

            // C5 — cost leaves the product object: the RPC writes the junction (write_junction_cost) and the
            // sync trigger carries cost_usd back to products. Each row carries the product fields + the cost
            // fields as their own keys. distributor_id/import_id are stamped by the RPC from its params.
            // W1 — supplier_id retired; manufacturer is on the import record (ingestion_batches.manufacturer_id),
            // NOT on products (ADR 48, manufacturer derives via brand).
            const { cost_usd: _omitCost, ...productNoCost } = product;
            const productFields: Record<string, unknown> = {
              ...productNoCost,
              source_row_number: headerRowIdx + globalIdx + 2,
              data_pulled_at: now,
            };
            const costSheet = typeof product.cost_usd === 'number' ? product.cost_usd : null;
            const costSheetBase = costBases[globalIdx] ?? null;

            if (decision.outcome === 'resolve' && decision.masterId) {
              // resolve-set row: add-only identity-fill + algopix_data/map_price refresh + smart-spread
              // provenance happen server-side; the living cost lands on the junction.
              updateById.set(decision.masterId, {
                master_id: decision.masterId,
                product: {
                  ...productFields,
                  provenance: buildProvenance(provenanceById.get(decision.masterId) ?? null),
                },
                cost_sheet: costSheet,
                cost_sheet_base: costSheetBase,
                cost_api: null,
                cost_reason: costReason,
              });
            } else if (decision.outcome === 'insert') {
              // insert-set row: a new flat standing master (the RPC stamps isg_sku / condition_grade='new' /
              // cost -> junction). The key collapses within-batch duplicates (the resolver ran client-side).
              const key = gtinKey ?? asinKey ?? `idx:${globalIdx}`;
              insertByKey.set(key, {
                product: {
                  ...productFields,
                  provenance: productProvenance,
                  arbi_status: 'new',
                },
                cost_sheet: costSheet,
                cost_sheet_base: costSheetBase,
                cost_api: null,
                cost_reason: costReason,
              });
            } else {
              // hold — ambiguous (>=2 masters) / no clean anchor -> intake_resolution_queue (the new hold
              // path; the price_sheet branch had NO hold pre-C4 — the live auto-insert defect). Collected +
              // batch-inserted after the loop (holds are rare on a clean catalog). Path-aware queue (C4 Phase
              // 3) routes a price_sheet hold to the standing RPCs.
              holdRows.push({
                import_id: importRec.id,
                source_row_number: headerRowIdx + globalIdx + 2,
                raw_row: product as unknown as Json,
                upc: asStr(product.upc),
                asin: asStr(product.asin),
                supplier_part_no: asStr(product.supplier_part_no),
                model: asStr(product.model),
                brand: asStr(product.brand),
                title: asStr(product.title),
                cost_usd: typeof product.cost_usd === 'number' ? product.cost_usd : null,
              });
              heldCount++;
            }
          }

          const insertSet = [...insertByKey.values()];
          const resolveSet = [...updateById.values()];

          // C5 — the subsume: ONE atomic set-based RPC per batch replaces the two direct products writes.
          // It materializes the already-deduped sets (product + junction, atomically) and returns BOTH
          // partitions' IDs so brand-matching scopes to inserts ∪ resolves.
          if (insertSet.length > 0 || resolveSet.length > 0) {
            const { data: rpcData, error: rpcErr } = await supabase.rpc(
              'create_import_materialization',
              {
                p_insert_set: insertSet as unknown as Json,
                p_resolve_set: resolveSet as unknown as Json,
                p_distributor_id: distributorId,
                p_import_id: importRec.id,
                p_source: PROVENANCE_SOURCE_SHEET_IMPORT,
              }
            );
            if (rpcErr) {
              const { friendly, technical } = translateError(rpcErr.message);
              rowErrors.push({
                row: `rows ${batchStart + 1}–${batchStart + batch.length}`,
                friendly,
                technical,
              });
            } else {
              insertedCount += insertSet.length;
              updatedCount += resolveSet.length;
              // both partitions feed match_product_brands — a resolve-only batch still drains its IDs.
              const ids = (rpcData ?? {}) as unknown as MaterializeResult;
              matchedIds.push(...(ids.inserted_ids ?? []), ...(ids.resolved_ids ?? []));
            }
          }

          const processed = Math.min(batchStart + BATCH_SIZE, totalRows);
          setProgress({
            current: processed,
            total: totalRows,
            pct: Math.round((processed / totalRows) * 100),
          });
          setErrors([...rowErrors]);

          // Yield to browser between batches
          await new Promise<void>((r) => setTimeout(r, 0));
        }

        // C4 — flush the funnel-held price_sheet rows to intake_resolution_queue (batched; rare on a clean
        // catalog). The path-aware queue (Phase 3) routes these to the standing RPCs, not the clearance ones.
        if (holdRows.length > 0) {
          const { error: holdErr } = await supabase
            .from('intake_resolution_queue')
            .insert(holdRows as never);
          if (holdErr) {
            const { friendly, technical } = translateError(holdErr.message);
            rowErrors.push({ row: `held ${holdRows.length} rows`, friendly, technical });
            heldCount = 0; // the held insert failed — don't report holds that didn't land
          }
        }

        // ── 7. Brand matching ─────────────────────────────────────────────────
        // RPC scopes to IDs accumulated from successful insert/update batches;
        // auto-resolves unknown_brand flags for newly-matched products
        // (status='resolved', resolved_at=now(), resolved_by=NULL system signature).
        // Skipped when matchedIds is empty (e.g., every batch errored) — the
        // RPC's bulk-scan branch (p_product_ids=NULL) is intentionally not used
        // here per Phase 1 E2 resolution to avoid catalog-wide side effects from
        // a per-import call.
        let matched = 0;
        let resolvedFlags = 0;
        if (matchedIds.length > 0) {
          const { data: matchResult } = await supabase.rpc('match_product_brands', {
            p_product_ids: matchedIds,
          });
          matched = (matchResult as { matched?: number } | null)?.matched ?? 0;
          resolvedFlags = (matchResult as { resolved_flags?: number } | null)?.resolved_flags ?? 0;
        }

        // ── 8. Finalise import record ─────────────────────────────────────────
        const finalStatus =
          rowErrors.length > 0 && insertedCount + updatedCount === 0 ? 'failed' : 'complete';

        const finalUpdate: ImportUpdate = {
          status: finalStatus,
          processed_count: insertedCount + updatedCount,
          error_log:
            rowErrors.length > 0 ? (rowErrors as unknown as ImportUpdate['error_log']) : null,
          completed_at: now,
        };
        await supabase.from('ingestion_batches').update(finalUpdate).eq('id', importRec.id);

        setSummary({
          total: totalRows,
          inserted: insertedCount,
          updated: updatedCount,
          skipped: skippedCount,
          held: heldCount,
          errors: rowErrors.length,
          matched,
          resolvedFlags,
          fileName,
          sheetName,
        });
        setErrors(rowErrors);
        setStatus('complete');
      } catch (err) {
        console.error('[useImport] fatal:', err);
        const message = err instanceof Error ? err.message : String(err);
        const { friendly, technical } = translateError(message);
        const fatal: ImportError[] = [{ row: 0, friendly, technical }];
        setErrors(fatal);
        setStatus('error');

        if (importIdRef.current) {
          const fatalUpdate: ImportUpdate = {
            status: 'failed',
            error_log: fatal as unknown as ImportUpdate['error_log'],
          };
          await supabase
            .from('ingestion_batches')
            .update(fatalUpdate)
            .eq('id', importIdRef.current);
        }
      }
    },
    [reset]
  );

  return { importFile, status, progress, summary, errors, reset };
}
